import { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, message } from 'antd';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faPlus } from '@fortawesome/free-solid-svg-icons';
import { Upload } from 'antd';
import UserService from '../../services/User';
import { getLocalUser, changePassword } from '../../auth/LocalAuthService';

const Profile = () => {
    const localUser = getLocalUser();
    const [profileForm] = Form.useForm();
    const [passwordForm] = Form.useForm();
    const [userId, setUserId] = useState<string>('');
    const [imageUrl, setImageUrl] = useState<string>('');
    const [profileLoading, setProfileLoading] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);

    const fetchMyInfo = useCallback(async () => {
        try {
            let info: any;
            if (localUser) {
                // Local auth: fetch from DB via localauth/me
                const { data } = await (await import('../../api/index')).default.get('/localauth/me');
                info = data;
            } else {
                info = await UserService.getMyUserInfo();
            }
            if (info?.id) setUserId(info.id);
            if (info?.imageUrl) setImageUrl(info.imageUrl);
            profileForm.setFieldsValue({
                firstName: info?.firstName || '',
                lastName: info?.lastName || '',
                email: info?.email || localUser?.email || '',
                phoneNumber: info?.phoneNumber || '',
                address: info?.address || '',
                department: info?.department || '',
                position: info?.position || '',
            });
        } catch {
            toast.error('Failed to load profile.');
        }
    }, [profileForm, localUser]);

    useEffect(() => {
        fetchMyInfo();
    }, [fetchMyInfo]);

    const handleProfileSubmit = async (values: any) => {
        setProfileLoading(true);
        try {
            if (localUser) {
                const apiInstance = (await import('../../api/index')).default;
                await apiInstance.patch('/localauth/me', {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    phoneNumber: values.phoneNumber,
                    address: values.address,
                    department: values.department,
                    position: values.position,
                });
            } else {
                await UserService.updateMe({ ...values, imageUrl });
                if (userId) await UserService.userPoriflePicture(userId, imageUrl);
            }
            message.success('Profile updated successfully.');
        } catch {
            toast.error('Failed to update profile.');
        } finally {
            setProfileLoading(false);
        }
    };

    const handlePasswordSubmit = async (values: any) => {
        if (!localUser?.userId) {
            message.error('No local account found.');
            return;
        }
        setPasswordLoading(true);
        try {
            await changePassword(localUser.userId, values.currentPassword, values.newPassword);
            message.success('Password changed successfully.');
            passwordForm.resetFields();
        } catch (error: any) {
            const msg = error?.response?.data?.message || 'Failed to change password.';
            message.error(msg);
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleUpload = async (options: any) => {
        try {
            const data = new FormData();
            data.append('Image', options.file);
            const api = (await import('../../api/index')).default;
            const { data: url } = await api.post('/user/image', data, {
                headers: { 'Content-Type': 'multipart/form-data', Accept: 'text/plain' },
            });
            setImageUrl(url);
            message.success('Photo uploaded.');
        } catch {
            message.error('Failed to upload photo.');
        }
    };

    return (
        <div style={{ paddingTop: 15, paddingBottom: 60 }}>
            <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 24px' }}>
                <h2 style={{ marginBottom: 32, fontSize: 24, fontWeight: 600 }}>Profile</h2>

                {/* Photo upload */}
                <div style={{ marginBottom: 32, display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                        width: 80, height: 80, borderRadius: '50%', background: '#f0f0f0',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0
                    }}>
                        {imageUrl
                            ? <img src={imageUrl} alt="profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <FontAwesomeIcon icon={faUser} size="2x" color="#bbb" />
                        }
                    </div>
                    <Upload
                        customRequest={handleUpload}
                        showUploadList={false}
                        accept="image/*"
                    >
                        <Button icon={<FontAwesomeIcon icon={faPlus} style={{ marginRight: 8 }} />}>
                            Change Photo
                        </Button>
                    </Upload>
                </div>

                {/* Personal Details */}
                <Form form={profileForm} layout="vertical" onFinish={handleProfileSubmit}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
                        <Form.Item label="First Name" name="firstName">
                            <Input placeholder="Enter First Name" />
                        </Form.Item>
                        <Form.Item label="Last Name" name="lastName">
                            <Input placeholder="Enter Last Name" />
                        </Form.Item>
                        <Form.Item label="Email" name="email">
                            <Input placeholder="Enter Email" disabled />
                        </Form.Item>
                        <Form.Item label="Phone Number" name="phoneNumber">
                            <Input placeholder="Enter Phone Number" />
                        </Form.Item>
                        <Form.Item label="Address" name="address">
                            <Input placeholder="Enter Address" />
                        </Form.Item>
                        <Form.Item label="Department" name="department">
                            <Input placeholder="Enter Department" />
                        </Form.Item>
                        <Form.Item label="Position" name="position">
                            <Input placeholder="Enter Position" />
                        </Form.Item>
                    </div>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={profileLoading}>
                            Save Profile
                        </Button>
                    </Form.Item>
                </Form>

                <hr style={{ margin: '40px 0', borderColor: '#f0f0f0' }} />

                {/* Change Password — only for local auth users */}
                {localUser && (
                    <>
                        <h3 style={{ marginBottom: 24, fontSize: 18, fontWeight: 600 }}>Change Password</h3>
                        <Form form={passwordForm} layout="vertical" onFinish={handlePasswordSubmit} style={{ maxWidth: 400 }}>
                            <Form.Item
                                label="Current Password"
                                name="currentPassword"
                                rules={[{ required: true, message: 'Please enter your current password.' }]}
                            >
                                <Input.Password placeholder="Enter current password" />
                            </Form.Item>
                            <Form.Item
                                label="New Password"
                                name="newPassword"
                                rules={[
                                    { required: true, message: 'Please enter a new password.' },
                                    { min: 8, message: 'Password must be at least 8 characters.' },
                                ]}
                            >
                                <Input.Password placeholder="Enter new password" />
                            </Form.Item>
                            <Form.Item
                                label="Confirm New Password"
                                name="confirmPassword"
                                dependencies={['newPassword']}
                                rules={[
                                    { required: true, message: 'Please confirm your new password.' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('newPassword') === value)
                                                return Promise.resolve();
                                            return Promise.reject(new Error('Passwords do not match.'));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password placeholder="Confirm new password" />
                            </Form.Item>
                            <Form.Item>
                                <Button type="primary" htmlType="submit" loading={passwordLoading}>
                                    Change Password
                                </Button>
                            </Form.Item>
                        </Form>
                    </>
                )}
            </div>
        </div>
    );
};

export default Profile;
